import { Left, Right } from '@icon-park/react';
import { type ForwardedRef, forwardRef, type HTMLProps, memo, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { CAROUSEL_LIGHTBOX_HEIGHT, type CAROUSEL_SIZES } from '@didaskill/domain/html/constants';
import type { CarouselSlideHastElement } from '@didaskill/domain/html/types';
import { globalMessages } from '@didaskill/ui/intl';

import { Lightbox } from '../../../../general/lightbox/Lightbox';
import messages from './Carousel.messages';
import * as styles from './Carousel.styles';

export type CarouselProps = Omit<HTMLProps<HTMLElement>, 'ref'> & {
  caption?: string;
  selectedSize: CAROUSEL_SIZES;
  slideNodes?: CarouselSlideHastElement[];
};

export const CarouselBase = forwardRef(
  (
    { caption, selectedSize, slideNodes, ...props }: CarouselProps,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    const intl = useIntl();
    const scrollRef = useRef<HTMLDivElement>(null);
    const showNavigation = (slideNodes?.length ?? 0) > 1;

    const scroll = useCallback((direction: 'prev' | 'next') => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollBy({
        left: direction === 'next' ? scrollRef.current.clientWidth : -scrollRef.current.clientWidth,
        behavior: 'smooth',
      });
    }, []);

    return (
      <figure
        {...props}
        ref={ref}
        aria-roledescription="carousel"
        aria-label={caption ?? intl.formatMessage(messages.CAROUSEL_ARIA_LABEL)}
      >
        <Lightbox>
          <div css={styles.container}>
            {showNavigation && (
              <button
                type="button"
                onClick={() => scroll('prev')}
                aria-label={intl.formatMessage(globalMessages.PREV_SLIDE)}
                css={styles.slideNavigationButton}
              >
                <Left size={24} />
              </button>
            )}
            <div ref={scrollRef} aria-live="polite" css={styles.scrollContainer(selectedSize)}>
              {slideNodes?.length
                ? slideNodes.map((child, index) => {
                    const { dataAlt, dataResizedUploadHref, dataOriginalUploadHref, dataRatio } =
                      child.properties;

                    return (
                      <div
                        role="button"
                        tabIndex={0}
                        key={dataResizedUploadHref}
                        aria-roledescription="slide"
                        aria-label={intl.formatMessage(messages.CAROUSEL_ITEM_ARIA_LABEL, {
                          index: index + 1,
                          total: slideNodes.length,
                        })}
                        css={styles.slide}
                      >
                        <Lightbox.Item
                          original={dataOriginalUploadHref}
                          thumbnail={dataResizedUploadHref}
                          width={CAROUSEL_LIGHTBOX_HEIGHT * parseFloat(dataRatio)}
                          height={CAROUSEL_LIGHTBOX_HEIGHT}
                        >
                          {({ ref: imgRef, open }) => (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                open(event);
                              }}
                              css={styles.imgContainer}
                            >
                              <img
                                ref={imgRef}
                                src={dataResizedUploadHref}
                                alt={dataAlt || caption}
                                css={styles.image}
                              />
                            </button>
                          )}
                        </Lightbox.Item>
                      </div>
                    );
                  })
                : null}
            </div>

            {showNavigation && (
              <button
                type="button"
                onClick={() => scroll('next')}
                aria-label={intl.formatMessage(globalMessages.NEXT_SLIDE)}
                css={styles.slideNavigationButton}
              >
                <Right size={24} />
              </button>
            )}
          </div>
        </Lightbox>

        {!!caption && <figcaption>{caption}</figcaption>}
      </figure>
    );
  },
);

CarouselBase.displayName = 'Carousel';

export const Carousel = memo(CarouselBase);
